import {useCallback, useEffect, useState} from 'react';

import {getUserDetails} from 'src/openapi/admin/apis/GetUserDetailsApi';
import {getUserInfo} from 'src/openapi/admin/apis/GetUserInfoApi';
import {getUserIntegrationInfo} from 'src/openapi/admin/apis/GetUserIntegrationInfoApi';
import {getLoginMetadata} from 'src/openapi/admin/apis/UserLoginMetadataApi';
import {useAppDispatch} from 'src/store/hooks';

import type {Error as ApiError} from 'src/openapi/admin/models/Error';
import type {LoginMetadata} from 'src/openapi/admin/models/LoginMetadata';
import type {UserDetails} from 'src/openapi/admin/models/UserDetails';
import type {UserIntegrationInfo} from 'src/openapi/admin/models/UserIntegrationInfo';
import type {UserProfileInfo} from 'src/openapi/admin/models/UserProfileInfo';

type DrawerDetails = {
    details: UserDetails,
    profile: UserProfileInfo,
    integration: UserIntegrationInfo,
    login: LoginMetadata,
};

export const useUserDrawerDetails = (userId: number, open = true) => {
    const dispatch = useAppDispatch();
    const [state, setState] = useState<{
        userId: number,
        data: DrawerDetails | null,
        error: ApiError | null,
    }>({userId, data: null, error: null});
    const [version, setVersion] = useState(0);
    const retry = useCallback(() => setVersion(value => value + 1), []);

    useEffect(() => {
        if (!open) {
            return;
        }
        const controller = new AbortController();
        const config = {signal: controller.signal};
        setState({userId, data: null, error: null});
        const load = async () => {
            try {
                const [details, profile, integration, login] = await Promise.all([
                    dispatch(getUserDetails({userId}, config)),
                    dispatch(getUserInfo({userId}, config)),
                    dispatch(getUserIntegrationInfo({userId}, config)),
                    dispatch(getLoginMetadata({userId}, config)),
                ]);
                if (!details.payload?.success) {
                    throw details.payload?.data;
                }
                if (!profile.payload?.success) {
                    throw profile.payload?.data;
                }
                if (!integration.payload?.success) {
                    throw integration.payload?.data;
                }
                if (!login.payload?.success) {
                    throw login.payload?.data;
                }
                if (!controller.signal.aborted) {
                    setState({userId, error: null, data: {
                        details: details.payload.data, profile: profile.payload.data,
                        integration: integration.payload.data, login: login.payload.data,
                    }});
                }
            } catch (error) {
                if (!controller.signal.aborted) {
                    setState({userId, data: null,
                        error: (error as ApiError) || {message: 'Unable to load user details.'}});
                }
            }
        };
        load();
        return () => controller.abort();
    }, [dispatch, userId, version, open]);

    return {data: state.userId === userId ? state.data : null,
        error: state.userId === userId ? state.error : null, retry,
        updateDetails: (details: UserDetails) => setState(value => value.data ?
                {...value, data: {...value.data, details}} : value)};
};
